import React, { useState } from "react";
import "./RoutePlanner.css";

const MAX_STOPS = 12;

const SAMPLE = {
  origin: "1280 Main St W, Hamilton, ON",
  stops: [
    "100 King St W, Hamilton, ON",
    "301 Bay St, Toronto, ON",
    "6301 Silver Dart Dr, Mississauga, ON",
    "1 Blue Jays Way, Toronto, ON",
    "50 Lakeshore Rd E, Oakville, ON",
  ],
  destination: "5555 Wellington Rd 27, Milton, ON",
};

const RoutePlanner = ({ onSubmit, isLoading }) => {
  const [origin, setOrigin] = useState("");
  const [stops, setStops] = useState(["", ""]);
  const [destination, setDestination] = useState("");

  const filledStops = stops.filter((stop) => stop.trim() !== "");
  const canSubmit =
    origin.trim() !== "" && destination.trim() !== "" && !isLoading;

  const updateStop = (index, value) =>
    setStops((prev) => prev.map((stop, i) => (i === index ? value : stop)));

  const addStop = () =>
    setStops((prev) => (prev.length < MAX_STOPS ? [...prev, ""] : prev));

  const removeStop = (index) =>
    setStops((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : [""]
    );

  const loadSample = () => {
    setOrigin(SAMPLE.origin);
    setStops(SAMPLE.stops);
    setDestination(SAMPLE.destination);
  };

  const reset = () => {
    setOrigin("");
    setStops(["", ""]);
    setDestination("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit([origin.trim(), ...filledStops.map((s) => s.trim()), destination.trim()]);
  };

  return (
    <section className="planner section" id="planner">
      <div className="container planner__inner">
        <div className="planner__head">
          <span className="eyebrow">Plan a route</span>
          <h2>Where are you going today?</h2>
          <p className="lede">
            Fill in the ends of the trip, then list the stops in whatever order
            they come to mind — the ordering is the part being solved for.
          </p>
          <button type="button" className="planner__sample" onClick={loadSample}>
            Load a sample route
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M4 10h11m0 0-4.2-4.2M15 10l-4.2 4.2"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <form className="planner__card" onSubmit={handleSubmit}>
          <div className="planner__route">
            <div className="field">
              <span className="field__marker field__marker--origin" aria-hidden="true" />
              <div className="field__body">
                <label htmlFor="origin">Origin</label>
                <input
                  id="origin"
                  type="text"
                  autoComplete="off"
                  placeholder="Where the day starts"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                />
              </div>
            </div>

            {stops.map((stop, index) => (
              // Inputs are positional and have no stable id, so index is the key.
              // eslint-disable-next-line react/no-array-index-key
              <div className="field" key={index}>
                <span className="field__marker" aria-hidden="true">
                  {index + 1}
                </span>
                <div className="field__body">
                  <label htmlFor={`stop-${index}`}>Stop {index + 1}</label>
                  <input
                    id={`stop-${index}`}
                    type="text"
                    autoComplete="off"
                    placeholder="An address along the way"
                    value={stop}
                    onChange={(e) => updateStop(index, e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="field__remove"
                  onClick={() => removeStop(index)}
                  aria-label={`Remove stop ${index + 1}`}
                >
                  <svg viewBox="0 0 20 20" fill="none">
                    <path
                      d="m6 6 8 8M14 6l-8 8"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            ))}

            <div className="field">
              <span
                className="field__marker field__marker--destination"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11Z"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="10" r="2.4" fill="currentColor" />
                </svg>
              </span>
              <div className="field__body">
                <label htmlFor="destination">Destination</label>
                <input
                  id="destination"
                  type="text"
                  autoComplete="off"
                  placeholder="Where the day ends"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="planner__addrow">
            <button
              type="button"
              className="planner__add"
              onClick={addStop}
              disabled={stops.length >= MAX_STOPS}
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M10 4.5v11M4.5 10h11"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              Add stop
            </button>
            <span className="planner__count">
              {stops.length >= MAX_STOPS
                ? `Maximum of ${MAX_STOPS} stops reached`
                : `${stops.length} of ${MAX_STOPS} stops`}
            </span>
          </div>

          <div className="planner__actions">
            <button className="btn planner__submit" type="submit" disabled={!canSubmit}>
              {isLoading ? "Optimising…" : "Find the shortest route"}
              {!isLoading && (
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path
                    d="M4 10h11m0 0-4.2-4.2M15 10l-4.2 4.2"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            <button type="button" className="planner__reset" onClick={reset}>
              Clear
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default RoutePlanner;
